'use client'

import { useState } from 'react'
import { startAuthentication } from '@simplewebauthn/browser'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { ShieldAlert } from 'lucide-react'

export type StepUpFactor = 'passkey' | 'password'

export interface StepUpFactorPickerProps {
  factors: StepUpFactor[]
  verifyReason: string
  passkeyOptions?: Record<string, unknown>
  onSubmit: (factor: StepUpFactor, payload: Record<string, unknown>) => Promise<void> | void
  onCancel: () => void
}

export function StepUpFactorPicker({
  factors,
  verifyReason,
  passkeyOptions,
  onSubmit,
  onCancel,
}: StepUpFactorPickerProps) {
  const [active, setActive] = useState<StepUpFactor>(factors[0])
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handlePasskey = async () => {
    setBusy(true)
    setError(null)
    try {
      if (!passkeyOptions) throw new Error('Passkey options are unavailable')
      const assertion = await startAuthentication({
        optionsJSON: passkeyOptions as unknown as Parameters<typeof startAuthentication>[0]['optionsJSON'],
      })
      await onSubmit('passkey', assertion as unknown as Record<string, unknown>)
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Passkey verification cancelled'
      setError(msg)
    } finally {
      setBusy(false)
    }
  }

  const handlePassword = async () => {
    setBusy(true)
    setError(null)
    try {
      await onSubmit('password', { password })
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Wrong password'
      setError(msg)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-4">
      <Alert>
        <ShieldAlert className="h-4 w-4" />
        <AlertDescription>{verifyReason}</AlertDescription>
      </Alert>

      <Tabs value={active} onValueChange={(v) => setActive(v as StepUpFactor)}>
        <TabsList
          className="grid w-full"
          style={{ gridTemplateColumns: `repeat(${factors.length}, 1fr)` }}
        >
          {factors.includes('passkey') && (
            <TabsTrigger value="passkey">Passkey</TabsTrigger>
          )}
          {factors.includes('password') && (
            <TabsTrigger value="password">Password</TabsTrigger>
          )}
        </TabsList>

        {factors.includes('passkey') && (
          <TabsContent value="passkey" className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Use your registered passkey (Touch ID, Windows Hello, security key).
            </p>
            <Button onClick={handlePasskey} disabled={busy} className="w-full">
              {busy ? 'Verifying…' : 'Verify with passkey'}
            </Button>
          </TabsContent>
        )}

        {factors.includes('password') && (
          <TabsContent value="password" className="space-y-3">
            <Label htmlFor="step-up-password">Account password</Label>
            <Input
              id="step-up-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
            <Button
              onClick={handlePassword}
              disabled={busy || !password}
              className="w-full"
            >
              {busy ? 'Verifying…' : 'Verify'}
            </Button>
          </TabsContent>
        )}

      </Tabs>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
      </div>
    </div>
  )
}
